import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { useSettingsDialog } from '../contexts/SettingsDialogContext';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountCircle from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import DevicesIcon from '@mui/icons-material/Devices';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import ContactsIcon from '@mui/icons-material/Contacts';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BrushIcon from '@mui/icons-material/Brush';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import MemoryIcon from '@mui/icons-material/Memory';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckIcon from '@mui/icons-material/Check';
import logoWhiteText from '../assets/morgan-rural-tech-logo.png';
import logoBlueText from '../assets/morgan-rural-tech-logo-blue.png';
import { useBranding } from '../contexts/BrandingContext';
import UserSettingsDialog from './UserSettingsDialog';

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItemData {
  id: string;
  name: string;
  description: string;
  icon: string;
  url: string;
  displayOrder: number;
  rolesAllowed: string[];
}

export const SIDEBAR_WIDTH = 260;
export const SIDEBAR_COLLAPSED_WIDTH = 72;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'dmo_sidebar_collapsed';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const auth = useAuth();
  const { mode, setMode, options: themeOptions } = useThemeMode();
  // The white-text wordmark bakes its text in white — invisible on the White
  // theme's light backgrounds — so swap in the blue-text variant there.
  const logo = mode === 'light' ? logoBlueText : logoWhiteText;
  // A client's branding (BrandingContext) replaces the header's logo and
  // "DMOTRAK" title; the sidebar keeps Morgan Rural Tech's logo.
  const { branding, available: brandings, canChoose: canChooseBranding, chooseBranding } = useBranding();
  const headerLogo = branding
    ? (mode === 'light' ? branding.logoOnLightUrl ?? branding.logoOnDarkUrl : branding.logoOnDarkUrl ?? branding.logoOnLightUrl) ?? logo
    : logo;
  const { open: settingsOpen, initialTab: settingsInitialTab, openSettings, closeSettings } = useSettingsDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItemData[]>([]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true');

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const fetchMenuItems = async () => {
    try {
      const response = await fetch('/api/menus/by-user-roles', {
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (response.ok) {
        const items = await response.json();
        setMenuItems(items);
      }
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    }
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.logout();
    handleClose();
  };

  const handleOpenSettings = () => {
    openSettings('profile');
    handleClose();
  };

  const handleNavigation = (url: string) => {
    navigate(url);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  };

  const isCollapsed = !isMobile && collapsed;
  const currentSidebarWidth = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const getIconForMenuItem = (name: string) => {
    const lowerName = name.toLowerCase();
    switch (lowerName) {
      case 'home':
        return <HomeIcon />;
      case 'my business':
        return <BusinessIcon />;
      case 'businesses':
        return <BusinessIcon />;
      case 'contacts':
        return <ContactsIcon />;
      case 'devices':
        return <DevicesIcon />;
      case 'reports':
        return <AssessmentIcon />;
      case 'image sorting':
        return <PhotoLibraryIcon />;
      case 'admin':
        return <AdminPanelSettingsIcon />;
      case 'help':
        return <HelpIcon />;
      case 'about':
        return <InfoIcon />;
      default:
        return <HomeIcon />;
    }
  };

  const renderNavItem = (opts: {
    key: string;
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    primary: string;
    secondary: string;
  }) => {
    const item = (
      <ListItem key={opts.key} disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          selected={opts.selected}
          onClick={opts.onClick}
          sx={isCollapsed ? { justifyContent: 'center', px: 2.5, py: '7px' } : { py: '7px' }}
        >
          <ListItemIcon
            sx={{
              color: opts.selected ? 'primary.main' : 'text.secondary',
              minWidth: isCollapsed ? 0 : 38,
              justifyContent: 'center',
            }}
          >
            {opts.icon}
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary={opts.primary}
              secondary={opts.secondary}
              primaryTypographyProps={{ fontWeight: opts.selected ? 600 : 400, color: opts.selected ? 'primary.main' : 'text.primary' }}
              secondaryTypographyProps={{ color: 'text.secondary', fontSize: '0.7rem' }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
    return isCollapsed ? (
      <Tooltip key={opts.key} title={opts.primary} placement="right">
        {item}
      </Tooltip>
    ) : item;
  };

  const sidebarContent = (
    <Box sx={{ width: currentSidebarWidth, transition: 'width 0.2s' }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, justifyContent: isCollapsed ? 'center' : 'space-between' }}>
        {!isCollapsed && (
          <img src={logo} alt="Morgan Rural Tech" style={{ height: 32, flexShrink: 0 }} />
        )}
        {!isMobile && (
          <IconButton size="small" onClick={toggleCollapsed} aria-label={isCollapsed ? 'expand sidebar' : 'collapse sidebar'}>
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) =>
          renderNavItem({
            key: item.id,
            selected: location.pathname === item.url,
            onClick: () => handleNavigation(item.url),
            icon: getIconForMenuItem(item.name),
            primary: item.name,
            secondary: item.description,
          })
        )}
        {auth.hasRole('DMO_ADMIN') &&
          renderNavItem({
            key: 'annotator',
            selected: location.pathname.startsWith('/annotator'),
            onClick: () => handleNavigation('/annotator/projects'),
            icon: <BrushIcon />,
            primary: 'Annotator',
            secondary: 'Image annotation & model training',
          })}
        {auth.hasRole('DMO_ADMIN') &&
          renderNavItem({
            key: 'models',
            selected: location.pathname === '/inference-models',
            onClick: () => handleNavigation('/inference-models'),
            icon: <ModelTrainingIcon />,
            primary: 'Models',
            secondary: 'Inference model registry',
          })}
        {auth.hasRole('DMO_ADMIN') &&
          renderNavItem({
            key: 'nodes',
            selected: location.pathname === '/inference-nodes',
            onClick: () => handleNavigation('/inference-nodes'),
            icon: <MemoryIcon />,
            primary: 'Nodes',
            secondary: 'GPU inference nodes',
          })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor="left"
        open={isMobile ? mobileMenuOpen : true}
        onClose={toggleMobileMenu}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : currentSidebarWidth,
          flexShrink: 0,
          transition: 'width 0.2s',
          '& .MuiDrawer-paper': { width: currentSidebarWidth, boxSizing: 'border-box', transition: 'width 0.2s', overflowX: 'hidden' },
        }}
      >
        {sidebarContent}
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar sx={{ position: 'relative' }}>
            {isMobile && (
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleMobileMenu}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <img src={headerLogo} alt={branding?.title ?? 'Morgan Rural Tech'} style={{ height: branding ? 36 : 28 }} />
              <Typography
                variant="h6"
                component="div"
                noWrap
                sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '0.03em', maxWidth: { xs: '45vw', md: 'none' } }}
              >
                {branding?.title ?? 'DMOTRAK'}
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary' }}>
                {auth.user?.email || auth.user?.fullName || 'User'}
              </Typography>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleClose}>
                  <Typography variant="body2">
                    {auth.user?.email || 'User'}
                  </Typography>
                </MenuItem>
                <Divider />
                <Typography variant="caption" sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}>
                  Theme
                </Typography>
                {themeOptions.map((option) => (
                  <MenuItem key={option.mode} selected={mode === option.mode} onClick={() => setMode(option.mode)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {mode === option.mode ? <CheckIcon fontSize="small" color="primary" /> : <PaletteIcon fontSize="small" sx={{ opacity: 0 }} />}
                    </ListItemIcon>
                    {option.label}
                  </MenuItem>
                ))}
                {canChooseBranding && brandings.length > 0 && [
                  <Divider key="branding-divider" />,
                  <Typography key="branding-caption" variant="caption" sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}>
                    Branding
                  </Typography>,
                  ...[null, ...brandings].map((b) => {
                    const selected = (branding?.businessId ?? null) === (b?.businessId ?? null);
                    return (
                      <MenuItem key={b?.businessId ?? 'default'} selected={selected} onClick={() => chooseBranding(b?.businessId ?? null)}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {selected ? <CheckIcon fontSize="small" color="primary" /> : <PaletteIcon fontSize="small" sx={{ opacity: 0 }} />}
                        </ListItemIcon>
                        {b ? b.title : 'Morgan Rural Tech (DMOTRAK)'}
                      </MenuItem>
                    );
                  }),
                ]}
                <Divider />
                <MenuItem onClick={handleOpenSettings}>Settings</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
      <UserSettingsDialog open={settingsOpen} onClose={closeSettings} initialTab={settingsInitialTab} />
    </Box>
  );
};

export default Layout;
